import { Helmet } from "react-helmet-async";

export default function WebEvaSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <Helmet>
        <title>Support - WebEva | Cosmovex</title>
        <meta
          name="description"
          content="Support page for WebEva, the HTML, CSS & JavaScript IDE by Cosmovex."
        />
        <link
          rel="canonical"
          href="https://cosmovexapps.com/apps/webeva/support"
        />
      </Helmet>

      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - WebEva
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, common answers, and contact details for WebEva users.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>WebEva</strong> is a mobile HTML, CSS, and JavaScript IDE
            for building, testing, and previewing web projects on the go.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I create a new project?
              </h3>
              <p className="mt-1">
                Tap the + button on the home screen and choose &quot;New
                Project&quot;. You can start from scratch or use a pre-built
                template. Projects can include HTML, CSS, JavaScript, and image
                files.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How does Live Preview work?
              </h3>
              <p className="mt-1">
                Tap the preview icon in the editor toolbar. The live server lets
                you view your page on another device connected to the same
                Wi-Fi network. Changes in your code are reflected automatically.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What does WebEva Premium include?
              </h3>
              <p className="mt-1">
                Premium is a monthly subscription that removes all ads, gives
                you AI credits for code generation, unlimited code snippets and
                tutorials, split-view editor, and offline mode.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What are AI Credits?
              </h3>
              <p className="mt-1">
                AI credits let you generate HTML, CSS, and JavaScript code
                using the built-in AI assistant. Each request deducts credits
                based on complexity. You can buy credit packs or get monthly
                credits with a Premium subscription.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I restore my purchase on a new device?
              </h3>
              <p className="mt-1">
                Sign in with the same Google or Apple account you used when you
                made the purchase. Your subscription or credits will sync
                automatically. In-app purchases are tied to the respective app
                store and cannot be transferred between platforms.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Is my data secure?
              </h3>
              <p className="mt-1">
                Your projects are stored locally on your device. Server-side
                data (account profile and purchase records) is protected by
                security rules that restrict access to your own data only.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I delete my account?
              </h3>
              <p className="mt-1">
                Email us at{" "}
                <a
                  href="mailto:trionapps@gmail.com?subject=WebEva%20Account%20Deletion"
                  className="text-[#0F6BBF] underline"
                >
                  trionapps@gmail.com
                </a>{" "}
                from the email associated with your account.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Support</h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Your device model and OS version</li>
            <li>Your app version</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Important Links</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/webeva/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                https://cosmovexapps.com/apps/webeva/privacy-policy
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">App Information</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>App Name:</strong> WebEva
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact Email:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-center text-sm text-[#8A93A2]">
          © {new Date().getFullYear()} Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
